import { useEffect, useMemo, useState } from 'react'
import { LogOut } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Section } from '../components/UI'
import { ActionToast } from '../components/ActionToast'
import { useTeamScope } from '../components/TeamScope'
import { supabase } from '../lib/supabase'

const dayOptions = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

export function Settings(){
  const { organisationId, selectedTeamId, teams, refreshTeams } = useTeamScope()
  const [saved,setSaved]=useState('')
  const [signingOut,setSigningOut]=useState(false)
  const [organisationName,setOrganisationName]=useState('')
  const [timezone,setTimezone]=useState('Africa/Johannesburg')
  const [scheduleTeamId,setScheduleTeamId]=useState('')
  const [startTime,setStartTime]=useState('08:00')
  const [endTime,setEndTime]=useState('17:00')
  const [workingDays,setWorkingDays]=useState<number[]>([1,2,3,4,5])
  const [savingSchedule,setSavingSchedule]=useState(false)
  const [savingOrganisation,setSavingOrganisation]=useState(false)
  const [error,setError]=useState('')
  const navigate=useNavigate()
  const save=(name:string)=>setSaved(`Saved ${name}.`)

  const scheduleTeam = useMemo(
    () => teams.find(team => team.id === scheduleTeamId) ?? null,
    [teams, scheduleTeamId]
  )

  useEffect(() => {
    if (!teams.length) {
      setScheduleTeamId('')
      return
    }

    if (selectedTeamId && teams.some(team => team.id === selectedTeamId)) {
      setScheduleTeamId(selectedTeamId)
      return
    }

    setScheduleTeamId(current => teams.some(team => team.id === current) ? current : teams[0].id)
  }, [teams, selectedTeamId])

  useEffect(() => {
    async function loadOrganisation() {
      if (!supabase || !organisationId) return
      const { data } = await supabase
        .from('organisations')
        .select('name,timezone')
        .eq('id', organisationId)
        .maybeSingle()
      if (data) {
        setOrganisationName(String(data.name ?? ''))
        setTimezone(String(data.timezone ?? 'Africa/Johannesburg'))
      }
    }
    loadOrganisation()
  }, [organisationId])

  useEffect(() => {
    async function loadSchedule() {
      if (!supabase || !scheduleTeamId || !scheduleTeam) return
      setStartTime(String(scheduleTeam.start_time).slice(0,5))
      setEndTime(String(scheduleTeam.end_time).slice(0,5))

      const { data, error: daysError } = await supabase
        .from('team_operating_days')
        .select('day_of_week,is_working,start_time,end_time')
        .eq('team_id', scheduleTeamId)
        .order('day_of_week', { ascending: true })

      if (daysError) {
        console.warn('[Mail Percent] Could not load team operating days', daysError)
        return
      }

      const activeDays = (data ?? [])
        .filter(row => row.is_working)
        .map(row => Number(row.day_of_week))
      setWorkingDays(data?.length ? activeDays : [1,2,3,4,5])
    }
    loadSchedule()
  }, [scheduleTeamId, scheduleTeam])

  function toggleDay(day:number){
    setWorkingDays(current => current.includes(day) ? current.filter(value => value !== day) : [...current,day].sort((a,b)=>a-b))
  }

  async function saveOrganisation(){
    if(!supabase || !organisationId || savingOrganisation)return
    setSavingOrganisation(true)
    setError('')
    const { error: updateError } = await supabase
      .from('organisations')
      .update({ name: organisationName.trim(), timezone })
      .eq('id', organisationId)
    setSavingOrganisation(false)
    if(updateError){setError(updateError.message);return}
    save('organisation settings')
  }

  async function saveSchedule(){
    if(!supabase || !scheduleTeamId || !scheduleTeam || savingSchedule)return
    if(!workingDays.length){setError('Choose at least one working day for this team.');return}
    if(endTime <= startTime){setError('Closing time must be later than opening time.');return}

    setSavingSchedule(true)
    setError('')

    const { error: teamError } = await supabase
      .from('teams')
      .update({ start_time:startTime, end_time:endTime })
      .eq('id', scheduleTeamId)

    if(teamError){setSavingSchedule(false);setError(teamError.message);return}

    const rows = dayOptions.map(day => ({
      team_id:scheduleTeamId,
      day_of_week:day.value,
      is_working:workingDays.includes(day.value),
      start_time:startTime,
      end_time:endTime,
      updated_at:new Date().toISOString()
    }))

    const { error: daysError } = await supabase
      .from('team_operating_days')
      .upsert(rows, { onConflict:'team_id,day_of_week' })

    setSavingSchedule(false)
    if(daysError){setError(daysError.message);return}

    refreshTeams()
    save(`${scheduleTeam.name} operating schedule`)
  }

  async function signOut(){
    if(!supabase)return
    setSigningOut(true)
    await supabase.auth.signOut()
    navigate('/login',{replace:true})
  }

  return <div className="stack-lg page-readable">
    {saved && <ActionToast title="Changes saved" message={saved} onDismiss={()=>setSaved('')}/>} 
    {error && <div className="notice notice-inline"><strong>Could not save:</strong><span>{error}</span></div>}
    <div className="page-brief"><div><span className="eyebrow">Workspace settings</span><h2>Set the customer communication standards your organisation wants the team to uphold.</h2><p>Configure your organisation, team operating hours and measurement policy in one clear place.</p></div></div>
    <div className="settings-readable-grid"><Section title="Organisation" subtitle="Workspace identity"><div className="form-grid"><label>Organisation name<input value={organisationName} onChange={event=>setOrganisationName(event.target.value)}/></label><label>Timezone<select value={timezone} onChange={event=>setTimezone(event.target.value)}><option>Africa/Johannesburg</option><option>UTC</option></select></label></div><button className="button primary" onClick={saveOrganisation} disabled={savingOrganisation}>{savingOrganisation?'Saving…':'Save changes'}</button></Section>
    <Section title="Customer email operating day" subtitle="Set the operating hours and working days used for response and SLA Performance reporting">
      {teams.length ? <>
        <label className="field settings-schedule-team-field" htmlFor="settings-schedule-team">
          <span>Team schedule</span>
          <select id="settings-schedule-team" value={scheduleTeamId} onChange={event=>setScheduleTeamId(event.target.value)}>
            {teams.map(team=><option key={team.id} value={team.id}>{team.name}</option>)}
          </select>
        </label>
        <div className="form-grid settings-operating-hours"><label>Opening time<input type="time" value={startTime} onChange={event=>setStartTime(event.target.value)}/></label><label>Closing time<input type="time" value={endTime} onChange={event=>setEndTime(event.target.value)}/></label></div>
        <div className="settings-working-days"><span>Operating days</span><div className="onboarding-day-toggle-grid">{dayOptions.map(day=><button type="button" key={day.value} className={workingDays.includes(day.value)?'active':''} onClick={()=>toggleDay(day.value)}>{day.label}</button>)}</div></div>
        <button className="button primary" onClick={saveSchedule} disabled={savingSchedule || !scheduleTeam}>{savingSchedule?'Saving…':'Save operating schedule'}</button>
      </> : <div className="settings-team-scope-empty"><strong>No teams yet</strong><span>Create a customer-email team before setting operating hours and days.</span></div>}
    </Section></div>
    <Section title="Measurement policy" subtitle="Choose how Mail Percent measures customer-email follow-through for this organisation"><div className="policy-list"><label className="policy-row"><div><strong>Provider response measurement</strong><span>Use Gmail API or Microsoft Graph timestamps to measure customer-email response cycles.</span></div><input type="checkbox" defaultChecked/></label><label className="policy-row"><div><strong>Server-side mailbox refresh</strong><span>Keep selected mailboxes refreshed server-side without employee browser setup.</span></div><input type="checkbox" defaultChecked/></label><label className="policy-row"><div><strong>AI response-standard analysis</strong><span>Check required meaning and components, not exact wording.</span></div><input type="checkbox" defaultChecked/></label></div><button className="button primary policy-save" onClick={()=>save('measurement policy')}>Save policy</button></Section>
    <Section title="Account" subtitle="End your current Mail Percent session"><div className="settings-signout-row"><div><strong>Sign out of this workspace</strong><span>You can sign in again at any time with your organisation account.</span></div><button className="button secondary" onClick={signOut} disabled={signingOut}><LogOut size={16}/>{signingOut ? <span className="blue-spinner button-spinner"/> : 'Sign out'}</button></div></Section>
  </div>
}
